/**
 * PillarBrief — shared standfirst block at the top of each Posture page.
 *
 * Mirrors the Command Center `<Standfirst>` pattern: kicker pill → italic-mixed
 * headline → deck → byline → ScoreBox in the right column. The single difference
 * is the `tone` prop, which selects the pillar accent (green / blue / sand)
 * via the `[data-tone]` accent map in `index.css`. ScoreBox itself is reused
 * unchanged — its tone-tinting is delegated to the wrapping `<section>`'s
 * `data-tone` attribute, which re-points the accent custom properties.
 *
 * Visual reference: 01-baseline-compliance.html / 02-security-center.html /
 * 03-user-analytics.html — the `.standfirst` block.
 */

import type { PostureBrief } from '@/lib/posture-brief'
import type { PillarTone } from '@/components/Dashboard/v2/PillarShell'
import ScoreBox from '@/components/Dashboard/v2/ScoreBox'
import { formatRelativeTime } from '@/lib/brief-helpers'

interface ScoreComponent {
  label: string
  /** Display weight string, e.g. "20%" — shown next to the label. */
  weight: string
  /** Score value (0-100). */
  value: number
  /** Tailwind utility for the fill — only `primary-500`, `secondary-blue-300`,
   *  `secondary-sand-300` are used (matches the mockup palette). */
  fillClass: string
}

interface Props {
  brief: PostureBrief
  /** Composite score (0-100). Pass `null` to show "—". Ignored when `aside`
   *  is provided. */
  score: number | null
  /** Component breakdown for the score box. Ignored when `aside` is
   *  provided. */
  components: ScoreComponent[]
  /** Pillar tone — selects the kicker dot, headline emphasis, and accent
   *  colors. Passed through to the wrapping `[data-tone]` so the ScoreBox
   *  picks up the same accent without per-tone props. */
  tone: PillarTone
  isLoading?: boolean
  /** Replace the right-rail ScoreBox with custom content. Use this on pages
   *  that aren't pillars in the Guardian composite, where a 0-100 score-box
   *  visual would falsely signal "this rolls into the headline score". */
  aside?: React.ReactNode
  /** Optional italic footer line under the byline. Use it to name the parent
   *  pillar a non-pillar page contributes to (e.g. "Findings here roll up
   *  into Best Practice."). */
  footerNote?: string
  /** Eyebrow label above the score number in the right-rail ScoreBox.
   *  Defaults to "Guardian Score". Per-pillar pages override this to name
   *  the pillar score actually rendered (e.g. "Best Practice Score") so
   *  the number doesn't read as the cross-pillar composite. Ignored when
   *  `aside` is provided. */
  scoreLabel?: string
  /** ISO timestamp of the most recent successful data sync for this page's
   *  underlying dataset. Renders as an editorial byline segment ("Updated
   *  12 minutes ago") so data freshness lives with the advisor signature
   *  instead of as a separate "Last sync" chip — per CLAUDE.md Product
   *  Voice rule #4 (byline carries provenance + scope).
   *
   *  - `null` / `undefined` (prop omitted) → no segment rendered. Pages
   *    that don't have a sync timestamp keep their byline unchanged.
   *  - `""` or an unparseable string → a graceful "Awaiting first sync."
   *    segment, so a brand-new tenant never sees a blank dash. */
  lastRefreshedAt?: string | null
}

/** Render a string with `**bold**` markers as <strong>. Lifted from
 *  `Standfirst.tsx` so the deck/byline copy can use the same emphasis
 *  shorthand the brief composers produce. */
function renderEmphasis(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-current">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}

export default function PillarBrief({
  brief,
  score,
  components,
  tone,
  isLoading,
  aside,
  footerNote,
  scoreLabel,
  lastRefreshedAt,
}: Props) {
  // Compose the byline segments. The base byline comes from the brief
  // composer ("Brief by Guardian Continuous Assessment · For {email}"); we
  // append a freshness segment so the user sees when the underlying data
  // was last refreshed. Note the explicit difference: the brief is *today's*
  // narrative — the sync timestamp is *the data's* age. Two adjacent
  // provenance facts, joined by the same · separator the byline already uses.
  const relative =
    lastRefreshedAt === undefined || lastRefreshedAt === null
      ? null
      : formatRelativeTime(lastRefreshedAt)
  let bylineSegments = brief.byline.split(' · ')
  if (lastRefreshedAt !== undefined && lastRefreshedAt !== null) {
    bylineSegments = [...bylineSegments, relative ?? 'Awaiting first sync.']
  }

  return (
    <section
      data-tone={tone}
      aria-labelledby="pillar-brief-headline"
      className="
        standfirst-rule
        grid grid-cols-1 gap-10 pb-10 pt-2
        lg:grid-cols-[1.5fr_1fr] lg:gap-14
      "
    >
      <div className="lg:pr-6">
        <span
          className="
            inline-flex items-center gap-2.5 rounded-[2px] bg-[#212938] px-3.5 py-1.5
            text-[10px] font-bold uppercase tracking-[0.28em] text-white
          "
        >
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full"
            style={{
              background: 'var(--tone-accent)',
              boxShadow: '0 0 6px var(--tone-accent)',
            }}
          />
          {brief.kicker}
        </span>

        <h1
          id="pillar-brief-headline"
          className="mt-5 font-display font-extrabold tracking-[-0.025em]"
          style={{
            fontSize: 'clamp(36px, 5vw, 60px)',
            lineHeight: 0.96,
            // --color-text-primary swaps with the theme so headlines stay
            // legible in light mode without us hard-coding navy.
            color: 'rgb(var(--color-text-primary))',
          }}
        >
          {brief.headlineSubject}{' '}
          <em
            style={{
              fontStyle: 'italic',
              fontWeight: 600,
              // Tone-deep accent. Each pillar substitutes via `[data-tone]`.
              color: 'var(--tone-accent-deep)',
            }}
          >
            {brief.headlineModifier}
          </em>
          {/* Period closes the first sentence; weakLink renders as a complete
              second sentence on the next line. Avoids the AI-coded em-dash. */}
          .
          <br />
          {brief.weakLink}
        </h1>

        <p
          className="mt-5 max-w-xl text-[16px] leading-[1.55]"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          {renderEmphasis(brief.deck)}
        </p>

        <div
          className="
            mt-6 flex flex-wrap gap-4 border-t pt-3.5
            text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500
          "
          style={{ borderTopColor: 'rgb(var(--color-dark-600))' }}
        >
          {bylineSegments.map((segment, i, arr) => (
            <span key={i} className="contents">
              <span>{renderEmphasis(segment)}</span>
              {i < arr.length - 1 && <span aria-hidden>·</span>}
            </span>
          ))}
        </div>

        {footerNote && (
          <p
            className="mt-3 text-[12px] italic"
            style={{ color: 'rgb(var(--color-text-secondary))' }}
          >
            {renderEmphasis(footerNote)}
          </p>
        )}
      </div>

      {aside ?? (
        <ScoreBox
          score={score}
          verdict={brief.verdict}
          delta={brief.delta}
          components={components}
          isLoading={isLoading}
          label={scoreLabel}
        />
      )}
    </section>
  )
}
